import React from 'react';
import { motion } from 'motion/react';
import { Target, Eye, Zap, Globe, Activity, Brain, Heart } from 'lucide-react';

const About = () => {
  // Update page title
  React.useEffect(() => {
    document.title = "About Us - Kodamalas Krushi Foundation | Our Mission & Vision";
  }, []);
  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  // Advanced animation variants for About page
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.15
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      rotateX: -15
    },
    visible: {
      opacity: 1,
      y: 0,
      rotateX: 0,
      transition: {
        type: "spring",
        damping: 80,
        stiffness: 400,
        duration: 0.6
      }
    }
  };

  const pillarsVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const pillarCardVariants = {
    hidden: { 
      opacity: 0, 
      x: -50,
      rotateY: -20 
    },
    visible: {
      opacity: 1,
      x: 0,
      rotateY: 0,
      transition: {
        type: "spring",
        damping: 100,
        stiffness: 500,
        duration: 0.8
      }
    }
  };

  const values = [
    {
      icon: Zap,
      title: "Innovation",
      description: "We continuously explore and implement the latest technological advancements to solve complex problems."
    },
    {
      icon: Target,
      title: "Impact-Driven",
      description: "Every solution we create is designed with measurable positive impact on communities in mind."
    },
    {
      icon: Globe,
      title: "Inclusivity",
      description: "We believe technology should be accessible to all, creating solutions that serve diverse populations."
    },
    {
      icon: Eye,
      title: "Vision-Focused",
      description: "We maintain a long-term perspective, building sustainable solutions for future generations."
    }
  ];

  return (
    <div className="min-h-screen pt-32 pb-20">
      {/* Hero Section with Background */}
      <section className="relative px-6 md:px-8 lg:px-12 mb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c" 
            alt="Team collaboration"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--bg-primary)] via-transparent to-[var(--bg-primary)]"></div>
        </div>
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="display-hero text-[var(--text-primary)] mb-6"
          >
            About Our Foundation
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springConfig, delay: 0.1 }}
            className="body-large text-[var(--text-secondary)]"
          >
            Kodamalas Krushi Foundation was established with a singular mission: to harness the transformative power of technology to solve pressing societal challenges and create meaningful, lasting impact in communities.
          </motion.p>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="px-6 md:px-8 lg:px-12 mb-20 bg-[var(--bg-light)] py-20">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="glass-card rounded-2xl p-10 border border-[var(--border-light)]"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-secondary)] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-[var(--brand-primary)]/30">
                <Eye size={32} className="text-[var(--bg-primary)]" />
              </div>
              <h2 className="heading-large text-[var(--text-primary)] mb-4">Our Vision</h2>
              <p className="body-large text-[var(--text-secondary)]">
                To build a better and inclusive world by harnessing the power of technology to solve real-world challenges and improve lives across communities.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="glass-card rounded-2xl p-10 border border-[var(--border-light)]"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-secondary)] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-[var(--brand-primary)]/30">
                <Target size={32} className="text-[var(--bg-primary)]" />
              </div>
              <h2 className="heading-large text-[var(--text-primary)] mb-4">Our Mission</h2>
              <p className="body-large text-[var(--text-secondary)]">
                To leverage technology to create sustainable, inclusive, and scalable solutions that address real-world problems and improve lives in society.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="px-6 md:px-8 lg:px-12 mb-20">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-12"
          >
            <h2 className="heading-large text-[var(--text-primary)] mb-4">What We Do</h2>
            <p className="body-large text-[var(--text-secondary)]">
              Kodamalas Krushi Foundation focuses on community development by identifying and solving problems through innovative technological solutions.
            </p>
          </motion.div>

          {/* Three Pillars of Child Development */}
          <div className="mb-8">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <motion.h3
                variants={cardVariants}
                className="heading-medium text-[var(--text-primary)] mb-4"
              >
                Our Three Pillars of Child Development
              </motion.h3>
              <motion.div
                variants={cardVariants}
                className="w-24 h-1 bg-gradient-to-r from-[var(--brand-primary)] via-[var(--brand-purple)] to-[var(--brand-coral)] mx-auto rounded-full"
              />
            </motion.div>
            
            <motion.div
              variants={pillarsVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-1 lg:grid-cols-3 gap-8"
            >
              {/* Physical Foundation - BoneAH */}
              <motion.div
                variants={pillarCardVariants}
                whileHover={{ 
                  scale: 1.05,
                  rotateY: 5,
                  rotateX: 5,
                  transition: { duration: 0.3 }
                }}
                className="glass-card rounded-2xl p-8 hover-lift border-l-4 border-l-[var(--brand-primary)] group perspective-1000 relative overflow-hidden"
              >
                {/* Animated background gradient */}
                <motion.div
                  animate={{
                    background: [
                      "linear-gradient(45deg, rgba(0, 208, 132, 0.1), transparent)",
                      "linear-gradient(45deg, rgba(0, 208, 132, 0.2), rgba(0, 208, 132, 0.05))",
                      "linear-gradient(45deg, rgba(0, 208, 132, 0.1), transparent)"
                    ]
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="absolute inset-0 opacity-50"
                />
                <div className="relative z-10">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-secondary)] rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                    <Activity size={24} className="text-white" />
                  </div>
                  <div>
                    <h4 className="body-large font-bold text-[var(--brand-primary)] mb-1 neon-green">
                      Physical Foundation
                    </h4>
                    <p className="text-sm text-[var(--text-muted)]">BoneAH - AI Diagnostics</p>
                  </div>
                </div>
                <p className="body-standard text-[var(--text-secondary)] mb-4">
                  Through our BoneAH tool, we use artificial intelligence to analyze physical growth patterns and identify potential health concerns in children, making advanced healthcare diagnostics accessible to all.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] px-3 py-1 rounded-full">AI-Powered</span>
                  <span className="text-xs bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] px-3 py-1 rounded-full">Early Detection</span>
                  <span className="text-xs bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] px-3 py-1 rounded-full">Health Monitoring</span>
                </div>
                </div>
              </motion.div>

              {/* Mental Excellence - IQplayground */}
              <motion.div
                variants={pillarCardVariants}
                whileHover={{ 
                  scale: 1.05,
                  rotateY: 5,
                  rotateX: 5,
                  transition: { duration: 0.3 }
                }}
                className="glass-card rounded-2xl p-8 hover-lift border-l-4 border-l-[var(--brand-purple)] group perspective-1000 relative overflow-hidden"
              >
                {/* Animated background gradient */}
                <motion.div
                  animate={{
                    background: [
                      "linear-gradient(45deg, rgba(167, 139, 250, 0.1), transparent)",
                      "linear-gradient(45deg, rgba(167, 139, 250, 0.2), rgba(167, 139, 250, 0.05))",
                      "linear-gradient(45deg, rgba(167, 139, 250, 0.1), transparent)"
                    ]
                  }}
                  transition={{ duration: 3, repeat: Infinity, delay: 1 }}
                  className="absolute inset-0 opacity-50"
                />
                <div className="relative z-10">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-[var(--brand-purple)] to-[#8B5CF6] rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                    <Brain size={24} className="text-white" />
                  </div>
                  <div>
                    <h4 className="body-large font-bold text-[var(--brand-purple)] mb-1 neon-purple">
                      Mental Excellence
                    </h4>
                    <p className="text-sm text-[var(--text-muted)]">IQplayground - Cognitive Growth</p>
                  </div>
                </div>
                <p className="body-standard text-[var(--text-secondary)] mb-4">
                  Our IQplayground application provides engaging, technology-driven activities that assess and enhance mental growth, ensuring comprehensive child development support through interactive learning experiences.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs bg-[var(--brand-purple)]/10 text-[var(--brand-purple)] px-3 py-1 rounded-full">Interactive Learning</span>
                  <span className="text-xs bg-[var(--brand-purple)]/10 text-[var(--brand-purple)] px-3 py-1 rounded-full">Cognitive Assessment</span>
                  <span className="text-xs bg-[var(--brand-purple)]/10 text-[var(--brand-purple)] px-3 py-1 rounded-full">Progress Tracking</span>
                </div>
                </div>
              </motion.div>

              {/* Emotional Wellbeing - Speak to the God */}
              <motion.div
                variants={pillarCardVariants}
                whileHover={{ 
                  scale: 1.05,
                  rotateY: 5,
                  rotateX: 5,
                  transition: { duration: 0.3 }
                }}
                className="glass-card rounded-2xl p-8 hover-lift border-l-4 border-l-[var(--brand-coral)] group perspective-1000 relative overflow-hidden"
              >
                {/* Animated background gradient */}
                <motion.div
                  animate={{
                    background: [
                      "linear-gradient(45deg, rgba(255, 107, 157, 0.1), transparent)",
                      "linear-gradient(45deg, rgba(255, 107, 157, 0.2), rgba(255, 107, 157, 0.05))",
                      "linear-gradient(45deg, rgba(255, 107, 157, 0.1), transparent)"
                    ]
                  }}
                  transition={{ duration: 3, repeat: Infinity, delay: 2 }}
                  className="absolute inset-0 opacity-50"
                />
                <div className="relative z-10">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-[var(--brand-coral)] to-[#FF4D7D] rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                    <Heart size={24} className="text-white" />
                  </div>
                  <div>
                    <h4 className="body-large font-bold text-[var(--brand-coral)] mb-1 neon-coral">
                      Emotional Wellbeing
                    </h4>
                    <p className="text-sm text-[var(--text-muted)]">Speak to the God - Spiritual Guidance</p>
                  </div>
                </div>
                <p className="body-standard text-[var(--text-secondary)] mb-4">
                  Our "Speak to the God" platform provides spiritual guidance and emotional wellbeing support, helping children find inner peace, build resilience, and develop strong emotional foundations for holistic growth.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs bg-[var(--brand-coral)]/10 text-[var(--brand-coral)] px-3 py-1 rounded-full">Spiritual Guidance</span>
                  <span className="text-xs bg-[var(--brand-coral)]/10 text-[var(--brand-coral)] px-3 py-1 rounded-full">Emotional Support</span>
                  <span className="text-xs bg-[var(--brand-coral)]/10 text-[var(--brand-coral)] px-3 py-1 rounded-full">Inner Peace</span>
                </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="px-6 md:px-8 lg:px-12 bg-[var(--bg-light)] py-20">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-12"
          >
            <h2 className="heading-large text-[var(--text-primary)] mb-4">Our Core Values</h2>
            <p className="body-large text-[var(--text-secondary)] max-w-3xl mx-auto">
              These principles guide every decision we make and every solution we build
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: index * 0.1 }}
                className="glass-card rounded-2xl p-8 hover-lift border border-[var(--border-light)]"
              >
                <div className="w-14 h-14 bg-[var(--brand-primary)] rounded-xl flex items-center justify-center mb-4">
                  <value.icon size={28} className="text-white" />
                </div>
                <h3 className="heading-medium text-[var(--text-primary)] mb-3">
                  {value.title}
                </h3>
                <p className="body-standard text-[var(--text-secondary)]">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Future Scope Section */}
      <section className="px-6 md:px-8 lg:px-12 py-20">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="bg-gradient-to-br from-[var(--bg-light)] to-white rounded-3xl p-10 text-center"
          >
            <h2 className="heading-large text-[var(--text-primary)] mb-4">
              Expanding Our Impact
            </h2>
            <p className="body-large text-[var(--text-secondary)] max-w-3xl mx-auto mb-6">
              While we currently focus on children's health and development, there is tremendous scope for applying technology to different aspects of community development. We continuously explore new opportunities to create meaningful impact across various societal domains.
            </p>
            <p className="body-standard text-[var(--text-secondary)] max-w-3xl mx-auto">
              Our vision extends beyond our current initiatives, aiming to address a wide spectrum of challenges using innovative technological solutions.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;